import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Channels } from '../../common/channelsEnum';
import { api } from '../../../utils/axiosConfig';
import { EmailerSendersGETResponse } from '../../../interfaces/backend/EmailerSendersGETResponse';
import { TemplatesPOSTRequest } from '../../../interfaces/backend/TemplatesPOSTRequest';
import { Template } from '../../../interfaces/backend/Template';
import { message } from 'antd';
import TemplatesList from './TemplatesList';
import { Notification } from '../../../interfaces/backend/Notifications';
import { TemplatesPATCHDefaultRequest } from '../../../interfaces/backend/TemplatesPATCHDefaultRequest';
export const matchSenderWithAllSenders = (
  senderEmail: string,
  allSenders: EmailerSendersGETResponse
): EmailerSendersGETResponse[number] | undefined => {
  const senderDomain = senderEmail.split('@')[1];

  // prioritize domains, I believe that's how SES works
  let sender = allSenders
    .filter((s) => s.type === 'domain')
    .find((s) => s.sender === senderDomain);
  if (sender) return sender;

  sender = allSenders
    .filter((s) => s.type === 'email')
    .find((s) => s.sender === senderEmail);

  return sender;
};

interface Props {
  notification: Notification;
}

const TemplatesListContainer = (props: Props): JSX.Element => {
  const { notificationId, accountId } =
    useParams<{ notificationId: string; accountId: string }>();
  const [templates, setTemplates] = useState<Template[]>();
  const [senders, setSenders] = useState<EmailerSendersGETResponse>();

  const fetchTemplates = useCallback(async (): Promise<void> => {
    const responses = await Promise.all(
      Object.values(Channels).map((channel) => {
        const url = `/${accountId}/notifications/${notificationId}/templates/${channel}`;
        return api('GET', url);
      })
    );

    const newTemplates = responses.map(
      (res) => (res as Record<'data', Template[]>).data
    );

    setTemplates(newTemplates.flat());
  }, [accountId, notificationId]);

  const deleteTemplate = async (
    channel: string,
    templateId: string
  ): Promise<void> => {
    await api(
      'DELETE',
      `/${accountId}/notifications/${notificationId}/templates/${channel}/${templateId}`
    );

    await fetchTemplates();
  };

  const makeDefault = async (
    channel: string,
    templateId: string,
    modes?: TemplatesPATCHDefaultRequest['modes']
  ): Promise<void> => {
    await api(
      'PATCH',
      `/${accountId}/notifications/${notificationId}/defaulttemplate/${channel}`,
      {
        templateId,
        modes
      } as TemplatesPATCHDefaultRequest
    );

    await fetchTemplates();
  };

  const patchTemplate = async (
    channel: string,
    templateId: string,
    params: Record<string, unknown>
  ): Promise<void> => {
    if (params['senderEmail'] && senders) {
      // if the template's sender does not match any sender on the account, start a domain verification process
      const senderEmail = params['senderEmail'] as string;
      const sender = matchSenderWithAllSenders(senderEmail, senders);
      if (!sender) {
        try {
          const res = await api('POST', `/${accountId}/emailer/senders`, {
            sender: senderEmail.split('@')[1]
          });
          setSenders([...senders, (res.data as EmailerSendersGETResponse)[0]]);
        } catch (e) {
          message.error('Something went wrong authenticating this domain!', 10);
          return;
        }
      }
    }

    await api(
      'PATCH',
      `/${accountId}/notifications/${notificationId}/templates/${channel}/${templateId}`,
      params
    );

    await fetchTemplates();
  };

  const createTemplate = async (
    channel: string,
    params: TemplatesPOSTRequest
  ) => {
    await api(
      'POST',
      `/${accountId}/notifications/${notificationId}/templates/${channel}`,
      params
    );

    await fetchTemplates();
  };

  useEffect(() => {
    api('GET', `/${accountId}/emailer/senders`).then((res) => {
      setSenders(res.data as EmailerSendersGETResponse);
    });

    fetchTemplates();
  }, [fetchTemplates, accountId]);

  return (
    <TemplatesList
      makeDefault={makeDefault}
      deleteTemplate={deleteTemplate}
      patchTemplate={patchTemplate}
      createTemplate={createTemplate}
      templates={templates as Extract<Template, { channel: 'INAPP_WEB' }>[]}
      notification={props.notification}
      senders={senders}
      activeChannels={props.notification.channels}
    />
  );
};

export default TemplatesListContainer;
